<script setup lang="ts">
  import { z } from 'zod';
  import { MpText } from '@borg/ui';
  import { CalculatorMode } from '@borg/types';
  import { isNumber, isObject, isString, objectKeys } from '@borg/utils';
  import { Author, Salary, Service } from '@borg/calc-croatia';
  import { queryString } from '@/utils/schemas';
  import CalculatorNavigation from '@/components/Calculator/CalculatorNavigation.vue';
  import '@borg/calc-croatia/dist/style.css';

  const props = defineProps<{
    mode: CalculatorMode;
  }>();

  const schemas = {
    'salary': z.object({
      amount: queryString.number.optional(),
      place: queryString.number.optional(),
      children: queryString.number.optional(),
      dependents: queryString.number.optional(),
      disability: queryString.number.optional(),
      type: queryString.string.optional(),
    }),
    'author': z.object({
      amount: queryString.number.optional(),
      place: queryString.number.optional(),
      authorType: queryString.number.optional(),
      type: queryString.string.optional(),
    }),
    'service': z.object({
      amount: queryString.number.optional(),
      place: queryString.number.optional(),
      type: queryString.string.optional(),
    }),
  };

  const elements = {
    salary: Salary,
    author: Author,
    service: Service,
  };

  const { t } = useI18n();
  const router = useRouter();

  const element = computed(() => elements[props.mode as keyof typeof elements]);
  const texts = computed<{ title: string; intro: string | undefined }>(() => {
    switch (props.mode) {
      case 'salary':
        return {
          title: t('SALARY_CALCULATOR_PAGE.TITLE'),
          intro: t('SALARY_CALCULATOR_PAGE.INTRO'),
        };
      case 'author':
        return {
          title: t('AUTHOR_CALCULATOR_PAGE.TITLE'),
          intro: t('AUTHOR_CALCULATOR_PAGE.INTRO'),
        };
      case 'service':
        return {
          title: t('SERVICE_CALCULATOR_PAGE.TITLE'),
          intro: t('SERVICE_CALCULATOR_PAGE.INTRO'),
        };
    }
  });

  const qsParseResult = schemas[props.mode].safeParse(useRoute().query);
  const qsParseData = qsParseResult.success ? qsParseResult.data : {};

  function onParamsChange(params: unknown) {
    if (!isObject(params)) {
      return;
    }
    const query = objectKeys(params).reduce<Record<string, string>>((acc, key) => {
      const value = params[key];
      if (isString(key) || isNumber(key)) {
        if (isNumber(value) && value) {
          acc[key] = value.toString();
        } else if (isString(value)) {
          acc[key] = value;
        }
      }
      return acc;
    }, {});
    router.replace({ query });
  }
</script>

<template>
  <div class="calc-page">
    <CalculatorNavigationMobile
      :mode="mode"
      class="calc-page__nav-mobile"
    />
    <div class="calc-page__header">
      <MpText as="h1">
        {{ texts.title }}
      </MpText>
      <div class="calc-page__intro">
        <MpText
          as="p"
          no-margin
        >
          {{ texts.intro }}
        </MpText>
      </div>
    </div>
    <div class="calc-page__body">
      <main>
        <component
          :is="element"
          :input="qsParseData"
          @change="onParamsChange"
        />
        <div class="calc-page__my-paycheck">
          <MyPaycheck hide-average-salary />
        </div>
      </main>
      <aside>
        <CalculatorNavigation />
        <MyPaycheck hide-average-salary />
      </aside>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .calc-page {
    &__header {
      width: 100%;
      @include screen-lg {
        max-width: calc(100% - 352px - var(--mp-space-70));
      }
    }

    &__my-paycheck {
      display: block;
      @include screen-lg {
        display: none;
      }
    }

    &__intro {
      display: flex;
      flex-direction: column;
      gap: var(--mp-space-70);
    }

    &__body {
      display: flex;
      justify-content: space-between;
      flex-direction: column-reverse;
      gap: var(--mp-space-70);
      margin-top: var(--mp-space-60);
      @include screen-lg {
        flex-direction: row;
      }

      main {
        flex: 1;
      }

      aside {
        display: none;
        width: 100%;
        max-width: 352px;
        @include screen-lg {
          display: flex;
          flex-direction: column;
          gap: var(--mp-space-70);
        }
      }
    }

    &__nav-mobile {
      margin-bottom: var(--mp-space-60);
      @include screen-lg {
        display: none;
      }
    }

    .center {
      display: flex;
      justify-content: center;
    }
  }
</style>
